/**
 * Murphy Door Configurator - Gate Card Coordinator
 *
 * PURPOSE: When a synthetic "gate card" answer hides a real category (e.g.
 * "Cabinets? -> None" hides Top/Bottom Cabinet Doors), auto-apply the real
 * "no doors" product to that category so Add to Cart completion still
 * reaches 100% -- allRequiredCategoryIdsArray has no "optional category"
 * concept, so a hidden-but-unapplied category would permanently block ATC.
 * Also resolves the second-layer fallback: Top/Bottom Knobs & Pulls are
 * hidden (via SELECTION_VISIBILITY_DEPS) whenever their parent Cabinet
 * Doors category has no real style, but have no auto-apply of their own --
 * without this, selecting "None" hid the knobs categories AND left them
 * permanently unapplied, locking progress/ATC at whatever percent they
 * represented (confirmed live 2026-08-11: stuck at 60%, Features tab
 * uncompletable).
 *
 * ALSO resets those same fallbacks when a category comes back into play
 * (e.g. "None" -> "Top Only"): without this, Top Cabinet Doors kept
 * showing "No Top Cabinet Doors" as a complete, deliberate-looking
 * selection for a position the user just said DOES have cabinets --
 * confirmed live (Daniel, 2026-08-11). Reuses the existing
 * SELECTION_USER_UNAPPLY_REQUESTED event (the same one the x button on an
 * applied thumbnail publishes) rather than a new state-mutation path.
 *
 * ALSO resolves GATE_COMBINATION_RESOLUTIONS: once every gate in a
 * combination group (Swing + Hinge) has an answer, deterministically
 * auto-applies the one matching Door Handing variant (e.g. Swing=in +
 * Hinge=left -> "Left Hand Inswing") through the same bulk-apply funnel --
 * architecturally distinct from the hide/show handling above (one gate's
 * answer hiding/showing DIFFERENT categories vs. two gates' answers
 * combining into one variant on the SAME category).
 *
 * ALSO RE-APPLIES those same fallbacks when the gate's answer is arrived at
 * WITHOUT changing (issue #1826). handleGateAnswerChanged is subscribed only
 * to business:gate_answer_changed, and conductor._setGateAnswer early-returns
 * without publishing it when the answer it is handed already matches the
 * stored one -- so a re-affirming click, and a reconciliation pass that
 * re-derives the answer already in sessionStorage, both produced NOTHING.
 * Live on production 2026-09-20: 'none' stored, a `?config=` restore that
 * omits the four categories it hides, and the door sat at 80% with those four
 * hidden AND incomplete, Add to Cart disabled, and a click on None publishing
 * no event at all. computeGateFallbackSelections is now the one place that
 * answers "which selections must exist for this answer", and
 * reconcileGateFallbacks fills in whichever of them the applied-selections map
 * is missing -- additively, idempotently, never over a real selection.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-auto-navigation-coordinator.js (Pattern #9)
 *
 * CCP COMPLIANCE:
 * - Event-driven coordination ONLY (no direct BL/cart-manager/price-calculator calls)
 * - PPD-First data access via conductor delegation (getVirtualizedProductsForCategory)
 * - Publishes business:cascade_apply_gate_defaults -- consumed by the EXISTING,
 *   unmodified handleSimplifiedDefaultsRequest() bulk-apply funnel in
 *   md-config-bl-cascade-auto-selection-coordinator.js (composition, not duplication)
 * - Publishes selection:user_unapply_requested -- consumed by the EXISTING,
 *   unmodified conductor handler that real x-button clicks already use
 * - Bootstrap barrier pattern for race protection (Golden Pattern #9)
 *
 * DATA FLOW:
 * business:gate_answer_changed -> resolve hidden categories' "no doors" handle
 *   (CATEGORY_VISIBILITY_DEPS.autoApplyOnHide) AND any dependent category's
 *   "no knobs" variant title (SELECTION_VISIBILITY_DEPS.autoApplyVariantTitleOnHide)
 *   -> conductor.getVirtualizedProductsForCategory() -> find matching handle/variant
 *   -> business:cascade_apply_gate_defaults -> (existing funnel) -> APPLY_SELECTION
 * business:gate_answer_changed -> for categories newly visible, check if
 *   their CURRENT applied selection IS one of the above fallbacks -> if so,
 *   selection:user_unapply_requested -> (existing conductor handler) -> reset
 * business:gate_answer_changed -> if gateId belongs to a
 *   GATE_COMBINATION_RESOLUTIONS group and every gate in that group now has
 *   an answer, resolve variantTitles[answers.join('_')] -> resolveVariantByTitle()
 *   -> business:cascade_apply_gate_defaults -> (SAME existing funnel) -> APPLY_SELECTION
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // PATTERN 15: CLEANUP ARRAY
  let cleanupHandlers = [];

  /**
   * Resolves a "no doors" product handle to its real variant id via conductor
   * delegation. These are single-variant products (per
   * CONSTANTS.LAYER_SUPPRESSION_DEPS's existing forward-consumer
   * md-config-bl-image-layer-coordinator.js), so variants[0] is the one to apply.
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} handle
   * @returns {{productId: string, variantId: string}|null}
   */
  function resolveNoDoorsSelection(conductor, categoryName, handle) {
    if (!conductor || !handle) return null;
    try {
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];
      const match = products.find((p) => p && p.handle === handle);
      if (!match || !match.variants || !match.variants.length) {
        isDev() &&
          console.warn(
            '[GateCardCoordinator] Could not resolve "no doors" product for category "' +
              categoryName +
              '" (handle: ' +
              handle +
              ')'
          );
        return null;
      }
      return { productId: match.id, variantId: match.variants[0].id };
    } catch (error) {
      isDev() &&
        console.error(
          '[GateCardCoordinator] resolveNoDoorsSelection failed:',
          error
        );
      return null;
    }
  }

  /**
   * Resolves a category's single composite product's variant by TITLE, not
   * product handle -- for categories with no standalone product-per-choice
   * the way Cabinet Doors has "no doors" handles; the choice is a variant
   * inside the one product getVirtualizedProductsForCategory() returns.
   * Two current callers: the "no knobs" fallback
   * (SELECTION_VISIBILITY_DEPS.autoApplyVariantTitleOnHide, e.g. "No Knobs
   * or Pulls") and the Swing+Hinge -> Door Handing resolution
   * (GATE_COMBINATION_RESOLUTIONS.variantTitles, e.g. "Left Hand Inswing").
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} variantTitle
   * @returns {{productId: string, variantId: string}|null}
   */
  function resolveVariantByTitle(conductor, categoryName, variantTitle) {
    if (!conductor || !variantTitle) return null;
    try {
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];
      const product = products[0];
      const variant =
        product && product.variants
          ? product.variants.find((v) => v && v.title === variantTitle)
          : null;
      if (!product || !variant) {
        isDev() &&
          console.warn(
            '[GateCardCoordinator] Could not resolve variant for category "' +
              categoryName +
              '" (title: ' +
              variantTitle +
              ')'
          );
        return null;
      }
      return { productId: product.id, variantId: variant.id };
    } catch (error) {
      isDev() &&
        console.error(
          '[GateCardCoordinator] resolveVariantByTitle failed:',
          error
        );
      return null;
    }
  }

  /**
   * True if categoryName's CURRENT applied selection is the "no doors"
   * fallback product (i.e. it was auto-applied by this module while the
   * category was hidden, not a real user choice).
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} fallbackHandle
   * @returns {boolean}
   */
  function isCurrentlyAutoAppliedDoorsFallback(
    conductor,
    categoryName,
    fallbackHandle
  ) {
    if (!conductor || !fallbackHandle) return false;
    const appliedMap = conductor.getAppliedSelectionsMap
      ? conductor.getAppliedSelectionsMap()
      : null;
    const applied = appliedMap ? appliedMap.get(categoryName) : null;
    if (!applied || !applied.productId) return false;
    const products = conductor.getVirtualizedProductsForCategory
      ? conductor.getVirtualizedProductsForCategory(categoryName) || []
      : [];
    const product = products.find((p) => p && p.id === applied.productId);
    return !!(product && product.handle === fallbackHandle);
  }

  /**
   * Same idea as isCurrentlyAutoAppliedDoorsFallback but for the "no
   * knobs" fallback, which is a VARIANT (matched by title) inside the
   * category's own composite product, not a standalone product handle.
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} variantTitle
   * @returns {boolean}
   */
  function isCurrentlyAutoAppliedKnobsFallback(
    conductor,
    categoryName,
    variantTitle
  ) {
    if (!conductor || !variantTitle) return false;
    const appliedMap = conductor.getAppliedSelectionsMap
      ? conductor.getAppliedSelectionsMap()
      : null;
    const applied = appliedMap ? appliedMap.get(categoryName) : null;
    if (!applied || applied.variantId == null) return false;
    const products = conductor.getVirtualizedProductsForCategory
      ? conductor.getVirtualizedProductsForCategory(categoryName) || []
      : [];
    const product = products[0];
    const variant =
      product && product.variants
        ? product.variants.find((v) => v && v.title === variantTitle)
        : null;
    return !!(variant && String(variant.id) === String(applied.variantId));
  }

  /**
   * Publishes SELECTION_USER_UNAPPLY_REQUESTED -- the SAME event the ×
   * button on an applied thumbnail publishes (md-config-core-event-handler.js).
   * Reusing it (rather than a new state-mutation path) means the category
   * resets to incomplete through the conductor's existing, already-correct
   * unapply handling: state clear, tab completion badge, URL persistence,
   * UI reset -- all for free.
   * @param {string} categoryId
   * @param {string} reasonTag - for the source field, debugging only
   */
  function publishStaleFallbackReset(categoryId, reasonTag) {
    if (!CoreEventBus) return;
    CoreEventBus.publish(CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED, {
      categoryId: categoryId,
      timestamp: Date.now(),
      source: 'GateCardCoordinator.resetStaleAutoApply.' + reasonTag,
    });
  }

  /**
   * The gate visibility map, via the conductor so an admin
   * selection_dependency_rule and the constant can never disagree between
   * here and the other sites that read the same map.
   * @returns {Object}
   */
  function getGateVisibilityDeps() {
    return (
      window.MurphyDoorConfigurator.conductor?._getGateVisibilityDeps?.() ||
      CONSTANTS.CATEGORY_VISIBILITY_DEPS ||
      {}
    );
  }

  /**
   * Same, for the second progressive-disclosure layer.
   * @returns {Object}
   */
  function getSelectionVisibilityDeps() {
    return (
      window.MurphyDoorConfigurator.conductor?._getSelectionVisibilityDeps?.() ||
      CONSTANTS.SELECTION_VISIBILITY_DEPS ||
      {}
    );
  }

  /**
   * 🚨 THE SINGLE SOURCE OF TRUTH for "which selections MUST exist when this
   * gate holds this answer".
   *
   * Extracted out of handleGateAnswerChanged for issue #1826. That handler
   * used to own this computation outright, and it is subscribed ONLY to
   * BUSINESS_GATE_ANSWER_CHANGED -- so the batch could only ever be produced
   * by an answer that CHANGED. Every path that arrives at an answer without
   * changing it (conductor._setGateAnswer's unchanged-answer early return: a
   * re-affirming click, a reconciliation pass re-deriving the answer already
   * stored) produced nothing, and the categories that answer hides stayed
   * hidden AND permanently unapplied -- progress 80%, checkSummaryComplete()
   * false, Add to Cart disabled, and clicking the answer again published
   * nothing at all. Confirmed on production 2026-09-20.
   *
   * Pure in the sense that matters: it READS (conductor delegation only) and
   * returns; it publishes nothing and mutates nothing, so both the
   * changed-answer path and the reconciliation path can call it and be
   * guaranteed to agree.
   *
   * OPTIONS (Epic B2, 2026-10-09). `preserveReal: true` is for a gate answer
   * that a RECONCILIATION pass wrote, never a click. With it:
   *   - a hidden door category whose current entry is a REAL product (not the
   *     gate's own "no doors" fallback) is left alone, and its knobs are left
   *     out of the second layer too -- a restore that wrote a real door must
   *     never have it overwritten by a guess about the gate;
   *   - a pick equal to what is already applied is skipped (no-op apply).
   * With no options the output is byte-identical to before (the click path).
   *
   * @param {string} gateId
   * @param {string} answer
   * @param {Object} conductor
   * @param {{preserveReal?: boolean}} [options]
   * @returns {{selections: Array<{categoryId: string, productId: string, variantId: string}>, hiddenCategoryNames: Set<string>}}
   */
  function computeGateFallbackSelections(gateId, answer, conductor, options) {
    const selections = [];
    const hiddenCategoryNames = new Set();
    if (!gateId || !answer || !conductor) {
      return { selections, hiddenCategoryNames };
    }
    const preserveReal = !!(options && options.preserveReal);
    const appliedMap =
      (preserveReal && conductor.getAppliedSelectionsMap?.()) || new Map();
    const alreadyApplied = (categoryName, resolved) => {
      const entry = appliedMap.get(categoryName);
      return !!(
        entry &&
        entry.variantId != null &&
        String(entry.variantId) === String(resolved.variantId)
      );
    };
    // Parents whose dependents get the "no knobs" fallback. Without
    // preserveReal this is exactly hiddenCategoryNames (unchanged behaviour).
    const knobHiddenParents = new Set();

    const gateDeps = getGateVisibilityDeps();
    const gateConfig = gateDeps ? gateDeps[gateId] : null;
    if (!gateConfig || !gateConfig.categories || !gateConfig.answers) {
      return { selections, hiddenCategoryNames };
    }

    const visibleCategories = gateConfig.answers[answer] || [];
    const autoApplyOnHide = gateConfig.autoApplyOnHide || {};

    gateConfig.categories.forEach((categoryName) => {
      if (visibleCategories.indexOf(categoryName) !== -1) return; // stays visible, nothing to auto-apply
      hiddenCategoryNames.add(categoryName);
      const handle = autoApplyOnHide[categoryName];
      if (preserveReal) {
        const entry = appliedMap.get(categoryName);
        const holdsReal =
          !!entry &&
          entry.productId != null &&
          !(
            handle &&
            isCurrentlyAutoAppliedDoorsFallback(conductor, categoryName, handle)
          );
        if (holdsReal) return; // a real door -- never overwrite, keep its knob
      }
      knobHiddenParents.add(categoryName);
      if (!handle) {
        // KNOWN GAP, deliberately left visible rather than redesigned here
        // (#1826). A gate category with no autoApplyOnHide entry -- e.g. one
        // an admin adds to a gate's `categories` without also adding its
        // "none" product handle -- is hidden with nothing applied to it, and
        // allRequiredCategoryIdsArray has no "optional category" concept, so
        // it blocks Add to Cart forever with no visible cause. This used to
        // be a bare `return`. It is still a bare return, but it now SAYS so.
        isDev() &&
          console.warn(
            '[GateCardCoordinator] Gate "' +
              gateId +
              '" hides category "' +
              categoryName +
              '" but has no autoApplyOnHide handle for it -- that category ' +
              'will stay hidden AND unapplied, which blocks Add to Cart.'
          );
        return;
      }
      const resolved = resolveNoDoorsSelection(conductor, categoryName, handle);
      if (
        resolved &&
        !(preserveReal && alreadyApplied(categoryName, resolved))
      ) {
        selections.push({
          categoryId: categoryName,
          productId: resolved.productId,
          variantId: resolved.variantId,
        });
      }
    });

    // Second layer: any SELECTION_VISIBILITY_DEPS category (e.g. Top Knobs
    // & Pulls) that depends on a category hidden above also needs its
    // "no knobs" fallback in this same batch, or it stays hidden AND
    // permanently unapplied -- see SELECTION_VISIBILITY_DEPS doc comment.
    const selectionDeps = getSelectionVisibilityDeps();
    Object.keys(selectionDeps).forEach((dependentCategory) => {
      const dep = selectionDeps[dependentCategory];
      if (!knobHiddenParents.has(dep.dependsOnCategory)) return;
      if (!dep.autoApplyVariantTitleOnHide) return;
      const resolved = resolveVariantByTitle(
        conductor,
        dependentCategory,
        dep.autoApplyVariantTitleOnHide
      );
      if (
        resolved &&
        !(preserveReal && alreadyApplied(dependentCategory, resolved))
      ) {
        selections.push({
          categoryId: dependentCategory,
          productId: resolved.productId,
          variantId: resolved.variantId,
        });
      }
    });

    return { selections, hiddenCategoryNames };
  }

  /**
   * Publishes a resolved fallback batch into the EXISTING, unmodified
   * handleSimplifiedDefaultsRequest() bulk-apply funnel in
   * md-config-bl-cascade-auto-selection-coordinator.js.
   * @param {Array} selections
   * @param {string} context
   * @param {string} source
   */
  function publishGateFallbackBatch(selections, context, source) {
    if (!CoreEventBus || !selections || selections.length === 0) return;
    CoreEventBus.publish(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS,
      {
        selections: selections,
        context: context,
        timestamp: Date.now(),
        source: source,
      }
    );
  }

  /**
   * Resets stale auto-applied fallbacks on categories the gate's answer makes
   * VISIBLE. Without this, "Cabinets? None -> Top Only" left Top Cabinet Doors
   * showing "No Top Cabinet Doors" as a complete, deliberate-looking selection
   * for a position the user just said DOES have cabinets -- confirmed live
   * (Daniel, 2026-08-11).
   *
   * `skipCategories` (Epic B2) is only ever non-empty on the reconciliation
   * recompute: categories a restore just WROTE are the customer's own picks,
   * so a link carrying a real door plus a deliberate "No Knobs or Pulls" keeps
   * its No Knobs instead of having it reset to incomplete.
   *
   * @param {Object} gateConfig
   * @param {string} answer
   * @param {Object} conductor
   * @param {Set<string>} [skipCategories]
   */
  function resetStaleFallbacksOnVisible(
    gateConfig,
    answer,
    conductor,
    skipCategories
  ) {
    const visibleCategories = gateConfig.answers[answer] || [];
    const autoApplyOnHide = gateConfig.autoApplyOnHide || {};
    const selectionDeps = getSelectionVisibilityDeps();
    const skipped = (categoryName) =>
      !!(skipCategories && skipCategories.has(categoryName));

    gateConfig.categories.forEach((categoryName) => {
      if (visibleCategories.indexOf(categoryName) === -1) return; // not visible, nothing to reset
      const handle = autoApplyOnHide[categoryName];
      if (
        handle &&
        !skipped(categoryName) &&
        isCurrentlyAutoAppliedDoorsFallback(conductor, categoryName, handle)
      ) {
        publishStaleFallbackReset(categoryName, 'cabinet_doors');
      }

      // Anything depending on this now-visible category (e.g. Top Knobs &
      // Pulls depends on Top Cabinet Doors) also needs its OWN stale
      // fallback reset -- it will become a live requirement again the
      // moment a real door style is chosen, same reasoning as the parent.
      Object.keys(selectionDeps).forEach((dependentCategory) => {
        const dep = selectionDeps[dependentCategory];
        if (dep.dependsOnCategory !== categoryName) return;
        if (!dep.autoApplyVariantTitleOnHide) return;
        if (skipped(dependentCategory)) return;
        if (
          isCurrentlyAutoAppliedKnobsFallback(
            conductor,
            dependentCategory,
            dep.autoApplyVariantTitleOnHide
          )
        ) {
          publishStaleFallbackReset(dependentCategory, 'knobs_pulls');
        }
      });
    });
  }

  /**
   * Handles BUSINESS_GATE_ANSWER_CHANGED. For every category the gate
   * controls that the new answer does NOT include, resolves and bulk-applies
   * the real "no doors" product so ATC completion is unaffected. Also
   * resolves and includes the "no knobs" fallback for any
   * SELECTION_VISIBILITY_DEPS category that depends on one of those newly-
   * hidden categories, in the SAME batch -- otherwise Top/Bottom Knobs &
   * Pulls stay hidden AND permanently unapplied, locking Add to Cart /
   * progress (confirmed live: Cabinets? -> None locked progress at 60%).
   *
   * 🚨 ROUTES ON conductor._getGateAnswerSource(gateId), NEVER on
   * eventData.source. The conductor stamps every CHANGED payload with
   * source 'conductor', whoever wrote the answer; it caches the real
   * provenance BEFORE publishing, so reading it here is current.
   *   'user'           -> the synchronous click path, unchanged.
   *   'reconciliation' -> a derived answer, usually written from inside a
   *                       cascade COMPLETED handler while the cascade
   *                       coordinator still holds its lock. Publishing here
   *                       would be dropped (footgun "A Batch Published From a
   *                       Cascade COMPLETED Handler Is Silently Dropped"), so
   *                       it is deferred to requestGateAnswerRecompute, which
   *                       re-reads live state and never overwrites a real door.
   * @param {Object} eventData - {gateId, answer, timestamp, source}
   */
  function handleGateAnswerChanged(eventData) {
    if (!bootstrapComplete) {
      pendingCalls.push({ fn: handleGateAnswerChanged, args: [eventData] });
      return;
    }
    if (!eventData || !eventData.gateId || !eventData.answer) return;

    const gateDeps = getGateVisibilityDeps();
    const gateConfig = gateDeps ? gateDeps[eventData.gateId] : null;
    if (!gateConfig) return;

    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor) {
      isDev() &&
        console.warn(
          '[GateCardCoordinator] Conductor not available, cannot resolve gate defaults'
        );
      return;
    }

    const answerSource =
      typeof conductor._getGateAnswerSource === 'function'
        ? conductor._getGateAnswerSource(eventData.gateId)
        : 'user';
    if (answerSource === 'reconciliation') {
      // Runs synchronously inside the restore's COMPLETED, so restoreSession
      // is still that restore's; requestGateAnswerRecompute copies it.
      requestGateAnswerRecompute(
        eventData.gateId,
        restoreSession.isRestore ? restoreSession.categories : null
      );
      return;
    }

    // ONE source of truth with reconcileGateFallbacks below -- see
    // computeGateFallbackSelections. This path is unchanged in behaviour:
    // same batch, same synchronous publish, same context string.
    const { selections } = computeGateFallbackSelections(
      eventData.gateId,
      eventData.answer,
      conductor
    );

    // Runs independent of the hide/apply batch above (still fires even when
    // selections.length === 0, e.g. answering the gate for the first time
    // with nothing previously auto-applied to reset).
    resetStaleFallbacksOnVisible(gateConfig, eventData.answer, conductor);

    if (selections.length === 0) return;

    publishGateFallbackBatch(
      selections,
      'gate_' + eventData.gateId + '_' + eventData.answer,
      'GateCardCoordinator'
    );
  }

  /**
   * Handles BUSINESS_GATE_ANSWER_CHANGED for gates that belong to a
   * GATE_COMBINATION_RESOLUTIONS group (Swing + Hinge -> one Door Handing
   * variant). Architecturally distinct from handleGateAnswerChanged above:
   * that resolves ONE gate's answer into hide/show of DIFFERENT categories;
   * this resolves TWO gates' answers together into ONE variant on the SAME
   * category, and only once every gate in the group has been answered.
   * No-ops (early return) for any gate not part of a combination group --
   * e.g. every 'cabinets' answer change reaches here and does nothing,
   * same as handleGateAnswerChanged no-ops for 'swing'/'hinge'.
   * @param {Object} eventData - {gateId, answer, timestamp, source}
   */
  function handleGateCombinationResolution(eventData) {
    if (!bootstrapComplete) {
      pendingCalls.push({
        fn: handleGateCombinationResolution,
        args: [eventData],
      });
      return;
    }
    if (!eventData || !eventData.gateId) return;

    const resolutions = CONSTANTS.GATE_COMBINATION_RESOLUTIONS || [];
    const resolution = resolutions.find(
      (r) => r.gateIds.indexOf(eventData.gateId) !== -1
    );
    if (!resolution) return;

    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor || !conductor._getGateAnswer) return;

    const answers = resolution.gateIds.map((gateId) =>
      conductor._getGateAnswer(gateId)
    );
    if (answers.some((answer) => !answer)) return; // wait until every gate in the group is answered

    const variantTitle = resolution.variantTitles[answers.join('_')];
    if (!variantTitle) {
      isDev() &&
        console.warn(
          '[GateCardCoordinator] No variant mapping for combination "' +
            answers.join('_') +
            '" on category "' +
            resolution.category +
            '"'
        );
      return;
    }

    const resolved = resolveVariantByTitle(
      conductor,
      resolution.category,
      variantTitle
    );
    if (!resolved) return;

    if (CoreEventBus) {
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS,
        {
          selections: [
            {
              categoryId: resolution.category,
              productId: resolved.productId,
              variantId: resolved.variantId,
            },
          ],
          context:
            'gate_combination_' +
            resolution.gateIds.join('_') +
            '_' +
            answers.join('_'),
          timestamp: Date.now(),
          source: 'GateCardCoordinator.combinationResolution',
        }
      );
    }
  }

  // ==========================================================================
  // GATE FALLBACK RECONCILIATION (issue #1826)
  // ==========================================================================
  //
  // 🚨 WHY A PUBLISH FROM HERE IS DEFERRED TO A MICROTASK, ALWAYS.
  //
  // CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS is consumed by
  // handleSimplifiedDefaultsRequest() in the cascade coordinator, which opens
  // with `if (cascadeInProgress) { ...; return; }` -- it DROPS the batch, and
  // says so only behind isDev(). The event bus executes its callbacks fully
  // SYNCHRONOUSLY, and the cascade coordinator publishes
  // CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED from inside its
  // `try`, while its `finally { cascadeInProgress = false }` has NOT run yet.
  // So anything this module publishes straight out of a cascade-completion
  // handler is published into a still-locked coordinator and silently
  // discarded -- the failure mode reads as "the fix did nothing", not as an
  // error.
  //
  // A microtask is the smallest thing that is definitely late enough: it runs
  // after the whole synchronous cascade stack unwinds (the `finally` included),
  // and still before the browser can deliver another user input event. Same
  // reasoning, and the same `Promise.resolve().then` idiom, as the coalesced
  // tooltip attach pass in md-config-ui-tooltip-manager.js. rAF would be wrong
  // for the usual reason (paused in a backgrounded tab).
  //
  // cascadeActive is the belt to that microtask's braces: it is tracked from
  // this module's OWN subscriptions to the cascade lifecycle events (never by
  // reading the other coordinator's state, which would be a CCP violation), and
  // covers the case of a cascade that genuinely spans an async boundary. When
  // it is set we defer instead of publishing, and drain on the next completion
  // or failure -- so a stuck flag self-heals on the next cascade rather than
  // wedging the reconcile forever.
  let cascadeActive = false;
  let reconcileScheduled = false;
  let reconcileDeferred = false;
  let pendingAllGates = false;
  const pendingGateIds = new Set();

  // Epic B2: gates whose RECONCILIATION-sourced answer changed and still need
  // their fallbacks recomputed (gateId -> categories a restore wrote, which
  // the recompute's stale reset must leave alone). Same microtask, same lock
  // tracking, same drain as the fill-only reconcile above.
  const pendingRecomputes = new Map();

  // Categories written by the most recent RESTORE cascade (a `?config=` link
  // or a "Build this" preset -- CONSTANTS.CASCADE_RESTORE_CONTEXTS), keyed on
  // the STEP events' `reason` and `cascadeSessionId`. Replaced on every
  // STARTED, so anything queued for later must take a COPY of `categories`.
  let restoreSession = {
    sessionId: null,
    isRestore: false,
    categories: new Set(),
  };

  // Context of the batch this module is publishing RIGHT NOW. The cascade
  // coordinator's REJECTED arrives synchronously inside that publish, so this
  // is how its handler knows what to redo (never by replaying the payload).
  let publishingRequest = null;

  const RECOMPUTE_THRESHOLD_MS = 16;

  function isCascadeRestoreContext(context) {
    const list = CONSTANTS.CASCADE_RESTORE_CONTEXTS;
    return (
      typeof context === 'string' &&
      Array.isArray(list) &&
      list.indexOf(context) !== -1
    );
  }

  function scheduleGateFallbackReconcile() {
    if (reconcileScheduled) return;
    if (cascadeActive) {
      reconcileDeferred = true;
      return;
    }
    reconcileScheduled = true;
    Promise.resolve().then(runPendingGateFallbackReconcile);
  }

  /**
   * Ask for a fallback reconcile. Coalesces: many requests in one turn produce
   * exactly one pass, and that pass re-reads live state, so nothing it computes
   * can be stale.
   * @param {string} [gateId] - omit to reconcile every gate
   */
  function requestGateFallbackReconcile(gateId) {
    if (gateId) {
      pendingGateIds.add(gateId);
    } else {
      pendingAllGates = true;
    }
    scheduleGateFallbackReconcile();
  }

  /**
   * Ask for a reconciliation-sourced answer's fallbacks to be recomputed
   * (Epic B2). Deferred and coalesced exactly like the fill-only reconcile:
   * the recompute re-reads the CURRENT answer and source when it runs, so a
   * customer click in between wins.
   * @param {string} gateId
   * @param {Set<string>|null} [protectedCategories] - restore-written categories
   */
  function requestGateAnswerRecompute(gateId, protectedCategories) {
    if (!gateId) return;
    const merged = pendingRecomputes.get(gateId) || new Set();
    if (protectedCategories) {
      protectedCategories.forEach((category) => merged.add(category));
    }
    pendingRecomputes.set(gateId, merged);
    scheduleGateFallbackReconcile();
  }

  function runPendingGateFallbackReconcile() {
    reconcileScheduled = false;
    if (cascadeActive) {
      // Still locked -- keep the accumulated request and retry on completion.
      reconcileDeferred = true;
      return;
    }
    const all = pendingAllGates;
    const ids = Array.from(pendingGateIds);
    const recomputes = Array.from(pendingRecomputes.entries());
    pendingAllGates = false;
    pendingGateIds.clear();
    pendingRecomputes.clear();

    // A recompute is a superset of the fill-only reconcile for its gate (it
    // fills empty hidden categories too), so that gate is not filled twice.
    const recomputedIds = recomputes.map((entry) => entry[0]);
    recomputes.forEach((entry) =>
      recomputeGateAnswerFallbacks(entry[0], entry[1])
    );

    if (recomputedIds.length === 0) {
      // Unchanged path.
      if (all) {
        reconcileGateFallbacks();
        return;
      }
      ids.forEach((id) => reconcileGateFallbacks(id));
      return;
    }
    if (all) {
      reconcileGateFallbacks(undefined, recomputedIds);
      return;
    }
    ids
      .filter((id) => recomputedIds.indexOf(id) === -1)
      .forEach((id) => reconcileGateFallbacks(id));
  }

  /**
   * Publishes a gate batch while remembering what asked for it, so a
   * synchronous REJECTED from the locked funnel can re-queue the REQUEST.
   * @param {Array} selections
   * @param {string} context
   * @param {string} source
   * @param {Object} request - {gateId?, protectedCategories?}
   */
  function publishTrackedGateBatch(selections, context, source, request) {
    publishingRequest = Object.assign({ context: context }, request || {});
    try {
      publishGateFallbackBatch(selections, context, source);
    } finally {
      publishingRequest = null;
    }
  }

  /**
   * Epic B2. Recomputes the fallbacks for a gate whose answer a
   * RECONCILIATION pass wrote. Runs from the coalesced microtask only, never
   * inside a cascade's call stack.
   *
   *   - Re-reads the answer AND its source now. If a customer click has
   *     landed since (source 'user'), the click path already did the work:
   *     skip.
   *   - computeGateFallbackSelections with preserveReal: a hidden door that
   *     holds a REAL product is never overwritten, and its knob is left alone.
   *   - The stale reset on visible categories skips whatever the restore
   *     wrote (a deliberate restored "No Knobs or Pulls" stays).
   *
   * @param {string} gateId
   * @param {Set<string>} protectedCategories
   */
  function recomputeGateAnswerFallbacks(gateId, protectedCategories) {
    const startTime =
      window.performance && window.performance.now
        ? window.performance.now()
        : Date.now();
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor || typeof conductor._getGateAnswer !== 'function') return;
      const answer = conductor._getGateAnswer(gateId);
      if (answer == null) return;
      const source =
        typeof conductor._getGateAnswerSource === 'function'
          ? conductor._getGateAnswerSource(gateId)
          : 'user';
      if (source !== 'reconciliation') {
        isDev() &&
          console.log(
            '[GateCardCoordinator] Recompute for gate "' +
              gateId +
              '" skipped -- the customer answered it since.'
          );
        return;
      }
      const gateDeps = getGateVisibilityDeps();
      const gateConfig = gateDeps ? gateDeps[gateId] : null;
      if (!gateConfig || !gateConfig.answers || !gateConfig.categories) return;

      const { selections } = computeGateFallbackSelections(
        gateId,
        answer,
        conductor,
        { preserveReal: true }
      );
      resetStaleFallbacksOnVisible(
        gateConfig,
        answer,
        conductor,
        protectedCategories
      );
      if (selections.length === 0) return;
      publishTrackedGateBatch(
        selections,
        'gate_' + gateId + '_' + answer,
        'GateCardCoordinator.reconciliationRecompute',
        { gateId: gateId, protectedCategories: protectedCategories }
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[GateCardCoordinator] recomputeGateAnswerFallbacks failed:',
          error
        );
    } finally {
      const duration =
        (window.performance && window.performance.now
          ? window.performance.now()
          : Date.now()) - startTime;
      if (duration > RECOMPUTE_THRESHOLD_MS) {
        isDev() &&
          console.warn(
            '[GateCardCoordinator] recomputeGateAnswerFallbacks took ' +
              duration.toFixed(1) +
              'ms (threshold ' +
              RECOMPUTE_THRESHOLD_MS +
              'ms)'
          );
      }
    }
  }

  /**
   * BUSINESS_CASCADE_APPLY_REQUEST_REJECTED for one of THIS module's batches
   * (context 'gate_*'). It arrives inside the funnel's own call stack and
   * proves the funnel still holds its lock, so cascadeActive is set first:
   * the re-request below then only FLAGS (reconcileDeferred) and is redone,
   * from live state, on the next cascade COMPLETED/FAILED drain. Clearing
   * cascadeActive first would schedule a microtask that can re-hit a lock
   * held across an async boundary, in a loop.
   * @param {Object} eventData
   */
  function handleGateBatchRejected(eventData) {
    const context = eventData && eventData.context;
    if (typeof context !== 'string' || context.indexOf('gate_') !== 0) return;
    cascadeActive = true;
    const request =
      publishingRequest && publishingRequest.context === context
        ? publishingRequest
        : null;
    if (request && request.gateId) {
      requestGateAnswerRecompute(request.gateId, request.protectedCategories);
    } else {
      // A click-path or fill batch: the additive fill-only reconcile is the
      // safe redo (it never overwrites anything).
      requestGateFallbackReconcile();
    }
    isDev() &&
      console.warn(
        '[GateCardCoordinator] Gate batch "' +
          context +
          '" rejected (cascade in progress) -- will recompute on the next completion.'
      );
  }

  /**
   * Cascade STEP: remember which categories a RESTORE cascade wrote.
   * @param {Object} eventData - {categoryId, cascadeSessionId, reason, ...}
   */
  function trackRestoreStep(eventData) {
    if (!eventData || !isCascadeRestoreContext(eventData.reason)) return;
    if (typeof eventData.categoryId !== 'string' || !eventData.categoryId) {
      return;
    }
    const sessionId =
      eventData.cascadeSessionId != null ? eventData.cascadeSessionId : null;
    if (sessionId !== restoreSession.sessionId) {
      restoreSession = {
        sessionId: sessionId,
        isRestore: true,
        categories: new Set(),
      };
    }
    restoreSession.categories.add(eventData.categoryId);
  }

  function drainDeferredGateFallbackReconcile() {
    if (!reconcileDeferred) return;
    reconcileDeferred = false;
    scheduleGateFallbackReconcile();
  }

  /**
   * Re-applies any gate fallback that SHOULD exist for the answer a gate
   * currently holds but does not exist in the applied-selections map.
   *
   * THE BUG THIS CLOSES (#1826, reproduced on production 2026-09-20, theme
   * 132070506541, 390x844). Answer the "Cabinets?" gate None in one tab
   * (sessionStorage mdConfiguratorGate_cabinets='none', __src='user'), then in
   * the SAME tab load a `?config=` share link whose JSON omits the four
   * categories that answer hides (Top/Bottom Cabinet Doors, Top/Bottom Knobs &
   * Pulls). The answer survives in sessionStorage, so:
   *
   *   - reconcileRestoredGateAnswers derives 'none' and calls _setGateAnswer,
   *     which early-returns on the unchanged answer -> no
   *     CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED -> handleGateAnswerChanged
   *     never runs -> the four fallbacks are never applied;
   *   - the progress denominator has no concept of gate visibility
   *     (md-config-core-data-pipeline.js / md-config-support-progress-tracker.js
   *     count all 20 required categories regardless), so progress sticks at
   *     16/20 = 80%, checkSummaryComplete() is false and Add to Cart is
   *     disabled;
   *   - the four categories are HIDDEN, so the customer cannot answer them;
   *   - and clicking None again published nothing at all, because that too is
   *     an unchanged answer. Measured: getEventHistory() carried no gate or
   *     cascade event for the click. A hard dead end.
   *
   * WHAT IT WILL AND WILL NOT DO. It only ever ADDS, and only to a category
   * that is BOTH hidden under the gate's current answer (it iterates exactly
   * what computeGateFallbackSelections produces, which is hidden-only by
   * construction) AND currently holds no applied selection at all. It never
   * unapplies, never touches a visible category, and never overwrites a real
   * selection -- so running it twice, or on a door that is already correct, is
   * a measured no-op.
   *
   * Reads the applied-selections MAP (conductor.getAppliedSelectionsMap), not
   * the snapshot: the two stores are allowed to disagree, and the Map is the
   * one a BROWSING transition can empty. Filling a category the Map says is
   * empty is the conservative direction for both.
   *
   * @param {string} [gateId] - omit to reconcile every gate
   * @param {Array<string>} [excludeGateIds] - gates a recompute already covered
   */
  function reconcileGateFallbacks(gateId, excludeGateIds) {
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor || typeof conductor._getGateAnswer !== 'function') return;

      const appliedMap =
        typeof conductor.getAppliedSelectionsMap === 'function'
          ? conductor.getAppliedSelectionsMap()
          : null;
      if (!appliedMap || typeof appliedMap.get !== 'function') return;

      const gateDeps = getGateVisibilityDeps();
      if (!gateDeps) return;

      const gateIds = gateId ? [gateId] : Object.keys(gateDeps);
      const missing = [];
      const seenCategories = new Set();

      gateIds.forEach((id) => {
        if (!gateDeps[id]) return;
        // A gate a recompute just handled: its batch already covers the fill.
        if (excludeGateIds && excludeGateIds.indexOf(id) !== -1) return;

        // An UNANSWERED gate hides its categories deliberately (see the
        // conductor's _clearGateAnswer doc comment: Add to Cart stays blocked
        // until the customer answers). Filling its fallbacks here would
        // silently complete a door behind a question nobody answered.
        const answer = conductor._getGateAnswer(id);
        if (answer == null) return;

        const { selections } = computeGateFallbackSelections(
          id,
          answer,
          conductor
        );
        selections.forEach((selection) => {
          if (seenCategories.has(selection.categoryId)) return;
          const entry = appliedMap.get(selection.categoryId);
          if (entry && entry.productId != null) return; // real selection -- never overwrite
          seenCategories.add(selection.categoryId);
          missing.push(selection);
        });
      });

      if (missing.length === 0) return;

      isDev() &&
        console.log(
          '[GateCardCoordinator] Re-applying ' +
            missing.length +
            ' missing gate fallback(s): ' +
            missing.map((s) => s.categoryId).join(', ')
        );

      publishTrackedGateBatch(
        missing,
        'gate_fallback_reconcile',
        'GateCardCoordinator.reconcileGateFallbacks',
        null
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[GateCardCoordinator] reconcileGateFallbacks failed:',
          error
        );
    }
  }

  /**
   * Derives an UNANSWERED gate's answer from restored applied selections
   * (URL config= recovery, cascade restoration) so a shared/bookmarked link
   * for an already-configured door doesn't leave its gate's real categories
   * (e.g. Top/Bottom Cabinet Doors) permanently hidden behind a question the
   * customer never gets shown.
   *
   * _getGateAnswer() reads ONLY sessionStorage (see its doc comment above) --
   * it has no concept of "the applied selections already answer this." A
   * fresh session loading `?config=` therefore has every gate at null even
   * when the config carries real selections for that gate's categories, and
   * a null answer is what the visibility manager and auto-navigation both
   * already treat as "hide the dependent categories" (see _clearGateAnswer's
   * doc comment above) -- so those categories stay invisible, and Add to
   * Cart stays hard-blocked per Daniel's 2026-08-25 rule, even though the
   * customer's own link already has a real answer sitting in the map.
   *
   * Runs at bootstrap on SYSTEM_INITIALIZED (same timing rationale as
   * reconcileRestoredGateCombinations below -- the cascade coordinator's
   * BUSINESS_CASCADE_APPLY_GATE_DEFAULTS subscription must already exist),
   * AND AGAIN on BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED.
   *
   * 🚨 THE SECOND PASS IS LOAD-BEARING, NOT DEFENSIVE PADDING. A `?config=`
   * link whose gate-controlled categories are composite products (e.g. Top/
   * Bottom Cabinet Doors, resolved by cascade material-matching against
   * Wood & Finish) can still be mid-restoration when SYSTEM_INITIALIZED
   * fires -- confirmed live 2026-09-12 via a sessionStorage.setItem stack
   * trace: reconcileRestoredGateAnswers ran with those two categories still
   * absent from the applied-selections map, saw 0 "real" categories, and
   * self-derived + persisted 'none' for a door whose actual answer (once
   * cascade restoration finished a moment later) was 'top_and_bottom' --
   * hiding both Cabinet Doors categories (their prices still counted toward
   * the total) behind a gate card showing the wrong answer. The first pass
   * is kept because most restorations (and the never-restoring, gate-
   * irrelevant common case) resolve well before SYSTEM_INITIALIZED, and gate
   * visibility should flip to correct as early as possible when they do.
   *
   * Idempotent/non-destructive in the sense that matters: a real customer
   * answer -- including a real 'none' click -- is never overwritten, tracked
   * via conductor._getGateAnswerSource() rather than mere presence, so THIS
   * function's own possibly-premature first-pass answer (source
   * 'reconciliation') stays open to correction by the second pass while any
   * answer produced by an actual click (source 'user', _setGateAnswer's
   * default) is protected the moment it lands. _setGateAnswer's own applied-
   * selections-based fallback/reset logic in handleGateAnswerChanged is a
   * no-op here by construction (the derived answer, by definition, already
   * matches what is applied).
   *
   * Written generically off CATEGORY_VISIBILITY_DEPS' shape -- no gate-
   * specific code, so a future gate needs zero changes here, matching the
   * existing single-source-of-truth rule this file documents at the top.
   *
   * 🚨 THE ONE EXCEPTION TO "never re-derive a 'user' answer" (Epic B2,
   * measured on prod 2026-10-09). A customer answers "Cabinets? None", then
   * opens a `?config=` link carrying Upper + Lower Shaker doors and Cranford
   * knobs: doors and knobs applied and charged ($571.80), gate still 'none',
   * both Cabinet Doors sections HIDDEN. A RESTORE puts back the customer's
   * own saved picks, so it may move an earlier answer -- but only when ALL of:
   *   - restoreContext is in CONSTANTS.CASCADE_RESTORE_CONTEXTS (a link or a
   *     "Build this" preset; a preset is a customer action too). Never
   *     willitfit_prefill / simplified_mode / a generic completion;
   *   - that restore actually WROTE at least one of this gate's categories
   *     (tracked from its STEP events). A link that omits them (#1826) keeps
   *     the customer's answer;
   *   - an EXACT derived answer exists.
   * The result is written with source 'reconciliation', so it stays
   * correctable and a later click upgrades it to 'user'.
   *
   * @param {string} [restoreContext] - the COMPLETED payload's context
   * @param {Set<string>} [restoredCategories] - categories that restore wrote
   */
  function reconcileRestoredGateAnswers(restoreContext, restoredCategories) {
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (
        !conductor ||
        typeof conductor._getGateAnswer !== 'function' ||
        typeof conductor._setGateAnswer !== 'function'
      ) {
        return;
      }

      const appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      if (!appliedMap) return;

      const gateDeps =
        conductor._getGateVisibilityDeps?.() ||
        CONSTANTS.CATEGORY_VISIBILITY_DEPS ||
        {};

      const restoreIsCustomerBuild = isCascadeRestoreContext(restoreContext);

      Object.keys(gateDeps).forEach((gateId) => {
        const gateConfig = gateDeps[gateId];
        if (!gateConfig || !gateConfig.categories || !gateConfig.answers) {
          return;
        }

        // Never overwrite a real customer answer -- including a real 'none'
        // click. An answer THIS function derived on an earlier pass (source
        // 'reconciliation') is fair game for correction; a 'user' answer only
        // under the restore exception documented above.
        const existingAnswer = conductor._getGateAnswer(gateId);
        if (
          existingAnswer != null &&
          conductor._getGateAnswerSource(gateId) !== 'reconciliation'
        ) {
          const restoreWroteGate =
            restoreIsCustomerBuild &&
            !!restoredCategories &&
            gateConfig.categories.some((categoryName) =>
              restoredCategories.has(categoryName)
            );
          if (!restoreWroteGate) return;
        }
        const autoApplyOnHide = gateConfig.autoApplyOnHide || {};

        // A category counts as "really answered" when it carries an applied
        // selection that is NOT the gate's own auto-applied "no doors"
        // fallback for it -- a category currently holding its own fallback
        // has not been answered by the customer, it just looks that way
        // because nothing has run for it yet either (same distinction
        // isCurrentlyAutoAppliedDoorsFallback already draws above).
        const realCategories = gateConfig.categories.filter((categoryName) => {
          const entry = appliedMap.get(categoryName);
          if (!entry || entry.productId == null) return false;
          const fallbackHandle = autoApplyOnHide[categoryName];
          if (!fallbackHandle) return true;
          return !isCurrentlyAutoAppliedDoorsFallback(
            conductor,
            categoryName,
            fallbackHandle
          );
        });

        const derivedAnswerKey = Object.keys(gateConfig.answers).find(
          (answerKey) => {
            const visibleForAnswer = gateConfig.answers[answerKey] || [];
            if (visibleForAnswer.length !== realCategories.length) {
              return false;
            }
            return visibleForAnswer.every(
              (categoryName) => realCategories.indexOf(categoryName) !== -1
            );
          }
        );

        // No exact match (data drifted from the enumerated answers) -- leave
        // unanswered rather than guess; the customer still sees the gate
        // card and can answer it themselves.
        if (!derivedAnswerKey) return;

        // Already correct (either a genuine first pass, or the second pass
        // confirming its own earlier answer still holds) -- _setGateAnswer's
        // own equality guard would no-op this anyway; skip the log noise.
        if (existingAnswer === derivedAnswerKey) return;

        isDev() &&
          console.log(
            '[GateCardCoordinator] Deriving gate "' +
              gateId +
              '" answer "' +
              derivedAnswerKey +
              '" from restored applied selections: ' +
              (realCategories.join(', ') || '(none)')
          );

        conductor._setGateAnswer(gateId, derivedAnswerKey, {
          source: 'reconciliation',
        });
      });
    } catch (error) {
      isDev() &&
        console.error(
          '[GateCardCoordinator] reconcileRestoredGateAnswers failed:',
          error
        );
    }

    // #1826. Deriving the answer is only half the job: an answer that is
    // ALREADY correct (the common case -- it survived in sessionStorage) takes
    // _setGateAnswer's unchanged-answer early return, so nothing computes the
    // fallbacks that answer's hidden categories need. Requested on BOTH passes,
    // outside the try/catch above so a throw while deriving answers cannot also
    // swallow the fill. Idempotent and coalesced, so the two passes cost one
    // microtask and one no-op.
    requestGateFallbackReconcile();
  }

  /**
   * Re-derives combination selections for gates that were already answered
   * before this page load.
   *
   * conductor._setGateAnswer() persists each answer to sessionStorage and
   * early-returns WITHOUT publishing when the answer it is handed matches the
   * one already held. On a reload the answer is restored from sessionStorage,
   * so every later re-assertion of that same value is silent and
   * handleGateCombinationResolution never runs. The gate ANSWER survives the
   * reload; the SELECTION it is supposed to produce does not.
   *
   * That leaves Door Handing unanswered AND display:none behind its own gate,
   * so auto-navigation targets it as first_incomplete, cannot scroll to a
   * hidden card, and strands the customer on whatever card they were on.
   * Observed live on 2026-08-24 after answering Swing/Hinge and reloading:
   * gate answers swing=out hinge=left present, zero applied Door Handing, and
   * the category re-opened twice with reason=first_incomplete.
   *
   * Runs once at bootstrap and is idempotent: a group whose category already
   * carries an applied selection is skipped, so a real user choice is never
   * overwritten.
   */
  function reconcileRestoredGateCombinations() {
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor || !conductor._getGateAnswer) return;

      const appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;

      (CONSTANTS.GATE_COMBINATION_RESOLUTIONS || []).forEach((resolution) => {
        // Never clobber an existing selection on this category.
        const alreadyApplied =
          appliedMap &&
          (typeof appliedMap.get === 'function'
            ? appliedMap.get(resolution.category)
            : appliedMap[resolution.category]);
        if (alreadyApplied) return;

        // Only act when EVERY gate in the group is already answered; a partly
        // answered group still has a real event coming.
        const answers = resolution.gateIds.map((id) =>
          conductor._getGateAnswer(id)
        );
        if (answers.some((a) => !a)) return;

        isDev() &&
          console.log(
            '[GateCardCoordinator] Re-deriving "' +
              resolution.category +
              '" from restored gate answers: ' +
              answers.join('_')
          );

        // Replay the same path a real answer takes, rather than duplicating it.
        handleGateCombinationResolution({
          gateId: resolution.gateIds[0],
          answer: answers[0],
          timestamp: Date.now(),
          source: 'restored_gate_reconciliation',
        });
      });
    } catch (error) {
      isDev() &&
        console.error(
          '[GateCardCoordinator] reconcileRestoredGateCombinations failed:',
          error
        );
    }
  }

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION, Golden Pattern #9)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              '[GateCardCoordinator] Processing ' +
                pendingCalls.length +
                ' queued calls'
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[GateCardCoordinator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    const unsubGateAnswerChanged = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED,
      handleGateAnswerChanged
    );
    cleanupHandlers.push(unsubGateAnswerChanged);

    const unsubGateCombination = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED,
      handleGateCombinationResolution
    );
    cleanupHandlers.push(unsubGateCombination);

    // SYSTEM_INITIALIZED, deliberately -- NOT SYSTEM_STATE_FROM_PPD_READY.
    //
    // The reconcile below ends in a BUSINESS_CASCADE_APPLY_GATE_DEFAULTS publish,
    // and its consumer in md-config-bl-cascade-auto-selection-coordinator.js
    // subscribes inside initialize(), which runs on SYSTEM_MAPS_INITIALIZED.
    // Measured on the dev theme 2026-08-24:
    //
    //   2088ms  system:state_from_ppd_ready
    //   2092ms  business:cascade_apply_gate_defaults   <- published to nobody
    //   2116ms  system:maps_initialized                <- consumer subscribes, 24ms late
    //   2371ms  system:initialized
    //
    // Publishing at ppd_ready looked correct and silently reached no subscriber.
    // That same 24ms seam is already documented two lines above that
    // subscription for curated defaults; this is the same trap, other direction.
    // SYSTEM_INITIALIZED is published exactly once (core-conductor) and is the
    // idiom the tooltip initializers and url-recovery-animator already use.
    //
    // reconcileRestoredGateAnswers runs FIRST, before reconcileRestoredGate-
    // Combinations: a gate answer it derives is a prerequisite input a future
    // combination resolution could depend on (GATE_COMBINATION_RESOLUTIONS is
    // empty today, so this is ordering for correctness, not a live bug fix).
    // Wrapped: the SYSTEM_INITIALIZED payload must not be read as a restore
    // context (only a cascade COMPLETED may pass one).
    const unsubRestoredGateAnswers = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_INITIALIZED,
      () => reconcileRestoredGateAnswers()
    );
    cleanupHandlers.push(unsubRestoredGateAnswers);

    // SECOND PASS, deliberately in addition to (not instead of) the one
    // above -- see reconcileRestoredGateAnswers' own doc comment for the
    // live incident this closes.
    //
    // 🚨 BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED is NOT restoration-only --
    // MUST be filtered, not subscribed to directly. cascade-auto-selection-
    // coordinator publishes this same event from THREE sites: the two that
    // matter here, handleUrlRestorationRequest (context: restorationSource,
    // e.g. 'url_restoration'/'willitfit_prefill') and
    // handleSimplifiedDefaultsRequest (context: 'simplified_mode') -- plus a
    // fully generic one, scheduleTermination(), fired whenever ANY cascade
    // completes (cascadeDepth > 0 or a forbidden auto-apply populated
    // autoAppliedCategories), including ordinary in-session material
    // preference/remap changes with no `?config=` or restoration involved
    // at all, and it carries NO `context` field. Subscribing
    // reconcileRestoredGateAnswers directly (as an earlier version of this
    // fix did) would re-run it on every one of those generic completions
    // for the rest of the page's life -- and while the function's own
    // guards make that safe against clobbering a real customer answer, nothing
    // rules out an unprompted gate-answer flip (and category reveal) later in
    // the session if some other cascade happens to leave a gate-governed
    // category holding a non-fallback selection. Filtering on `context` keeps
    // this pass scoped to what it exists for -- an actual restoration/prefill/
    // simplified-mode completion -- and turns the generic-completion case into
    // a true no-op rather than a defended-against one.
    function reconcileRestoredGateAnswersOnCascadeComplete(eventData) {
      // 🚨 The lock is released FIRST, before the context filter below can
      // return -- a cascade that completed has completed whatever its context
      // says, and a deferred fallback reconcile waiting on this flag must not
      // be stranded by a generic completion. See the GATE FALLBACK
      // RECONCILIATION block above for why this module tracks the lock at all.
      cascadeActive = false;
      if (eventData && eventData.context) {
        // Epic B2: the context and the categories this restore wrote decide
        // whether a 'user' answer may be re-derived; the same set protects
        // those categories from the recompute's stale reset.
        const sessionId = eventData.cascadeSessionId;
        const sameSession =
          sessionId == null || sessionId === restoreSession.sessionId;
        reconcileRestoredGateAnswers(
          eventData.context,
          sameSession ? restoreSession.categories : new Set()
        );
      }
      drainDeferredGateFallbackReconcile();
    }
    const unsubCascadeRestoreComplete = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
      reconcileRestoredGateAnswersOnCascadeComplete
    );
    cleanupHandlers.push(unsubCascadeRestoreComplete);

    // Cascade lifecycle, tracked locally and event-driven (never by reading
    // cascade-auto-selection-coordinator's state, which CCP forbids between
    // two bl- modules). Only used to decide whether a fallback publish would
    // land in a locked coordinator and be dropped.
    const unsubCascadeStarted = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
      (eventData) => {
        cascadeActive = true;
        // Epic B2: a new cascade starts a new restore record.
        const startContext =
          eventData && (eventData.triggerSource || eventData.context);
        restoreSession = {
          sessionId:
            eventData && eventData.sessionId != null
              ? eventData.sessionId
              : null,
          isRestore: isCascadeRestoreContext(startContext),
          categories: new Set(),
        };
      }
    );
    cleanupHandlers.push(unsubCascadeStarted);

    // Epic B2: which categories a RESTORE cascade wrote (STEP `reason` is the
    // restorationSource: 'url_restoration' / 'build_preset' / ...).
    const unsubCascadeStep = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
      trackRestoreStep
    );
    cleanupHandlers.push(unsubCascadeStep);

    // Epic B2: a gate batch the locked funnel turned away is only FLAGGED
    // here and recomputed on the next completion -- never re-published from
    // inside the funnel's call stack.
    const unsubGateBatchRejected = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_REQUEST_REJECTED,
      handleGateBatchRejected
    );
    cleanupHandlers.push(unsubGateBatchRejected);

    const unsubCascadeFailed = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
      () => {
        cascadeActive = false;
        drainDeferredGateFallbackReconcile();
      }
    );
    cleanupHandlers.push(unsubCascadeFailed);

    // A REAL click on the answer the gate already holds. The conductor's
    // _setGateAnswer publishes this instead of BUSINESS_GATE_ANSWER_CHANGED
    // when the answer is unchanged, so the customer's re-affirmation can
    // restore the fallbacks that answer needs WITHOUT re-running every
    // subscriber of a genuine answer change. Issue #1826: before this, the
    // re-click was the last remaining escape hatch out of a stuck 80% door and
    // it published nothing at all.
    const unsubGateAnswerReaffirmed = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_REAFFIRMED,
      (eventData) => {
        if (!eventData || !eventData.gateId) return;
        requestGateFallbackReconcile(eventData.gateId);
      }
    );
    cleanupHandlers.push(unsubGateAnswerReaffirmed);

    const unsubRestoredGates = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_INITIALIZED,
      reconcileRestoredGateCombinations
    );
    cleanupHandlers.push(unsubRestoredGates);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================
  window.MurphyDoorConfigurator.gateCardCoordinator = {
    cleanup: function () {
      cleanupHandlers.forEach((unsub) => {
        try {
          if (typeof unsub === 'function') unsub();
        } catch (error) {
          isDev() &&
            console.error('[GateCardCoordinator] Cleanup error:', error);
        }
      });
      cleanupHandlers = [];
    },
    getState: function () {
      return Object.freeze({
        bootstrapComplete: bootstrapComplete,
        pendingCallCount: pendingCalls.length,
        subscriptionCount: cleanupHandlers.length,
        cascadeActive: cascadeActive,
        reconcileScheduled: reconcileScheduled,
        reconcileDeferred: reconcileDeferred,
        pendingRecomputeCount: pendingRecomputes.size,
        restoredCategoryCount: restoreSession.categories.size,
      });
    },

    // Exposed for unit tests (#1826): the single source of truth for "which
    // selections must exist when this gate holds this answer", and the
    // idempotent filler that re-applies the missing ones. Both are also
    // reachable from the console when diagnosing a stuck-below-100% door.
    _gateFallbackTestHelpers: {
      computeGateFallbackSelections: computeGateFallbackSelections,
      reconcileGateFallbacks: reconcileGateFallbacks,
      requestGateAnswerRecompute: requestGateAnswerRecompute,
    },
  };

  isDev() && console.log('[GateCardCoordinator] Module loaded');
})(window);
